/**
 * StepSlider - Standalone step slider control
 *
 * A reusable step slider component that can be placed in any layout slot.
 * Handles step navigation with keyboard shortcuts and slider UI.
 */

// @todo: Migrate to 'Slider' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import {SliderKeyboardOperation} from '@wandb/weave/common/util/media';
import {clamp} from 'lodash';
import React, {useCallback} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import {useSectionSettingsContextMaybe} from '../../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useMaybeWorkspaceSettingsContext} from '../../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {MediaBrowserPanelProps} from '../Component';

// ============================================================================
// Types
// ============================================================================

export type StepSliderProps = {
  min: number;
  max: number;
  current: number;
  steps: number[];
  sliderKey?: string;
  label?: string;
  snapToExistingStep?: boolean;
  strideLength?: number;
  keyboardBindings?: {
    [key: string]: SliderKeyboardOperation;
  };
  onChange: (value: number) => void;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  'data-test'?: string;
};

/**
 * Renders a step slider control with label and tooltip.
 *
 * @param props - StepSliderProps
 * @returns React element
 */
export const StepSlider: React.FC<StepSliderProps> = ({
  min,
  max,
  current,
  steps,
  sliderKey = '_step',
  label = 'Step',
  snapToExistingStep = true,
  strideLength = 1,
  keyboardBindings,
  onChange,
  updateConfig,
  'data-test': dataTest = 'step-slider',
}) => {
  const sectionSettingsContext = useSectionSettingsContextMaybe();
  const setSectionSliderSteps = sectionSettingsContext?.setSectionSliderSteps;
  const workspaceSettingsContext = useMaybeWorkspaceSettingsContext();
  const setWorkspaceSliderSteps =
    workspaceSettingsContext?.media?.setWorkspaceSliderSteps;

  const stepSliderKeyIsStep = !sliderKey || sliderKey === '_step';

  // Part of the fix to get slider ticks to show up with non-uniform steps
  const shouldStepSliderAllowAnyValue = steps.some(step => step % 1 !== 0);

  const handleChange = useCallback(
    (value: number) => {
      const newValue = clamp(value, min, max);
      onChange(newValue);

      // Update section and workspace settings
      setSectionSliderSteps?.(newValue, sliderKey);
      setWorkspaceSliderSteps?.(
        newValue,
        sliderKey,
        sectionSettingsContext?.sectionMediaSettings?.sliderSync ?? false
      );
    },
    [
      min,
      max,
      onChange,
      setSectionSliderSteps,
      setWorkspaceSliderSteps,
      sliderKey,
      sectionSettingsContext,
    ]
  );

  return (
    <div className="control-popup__control-slider" data-test={dataTest}>
      <Popup
        trigger={<label data-test={`${dataTest}-label`}>{label}</label>}
        popperModifiers={{
          preventOverflow: {
            // prevent popper from erroneously constraining the popup to the
            // table header
            boundariesElement: 'viewport',
          },
        }}
        disabled={!stepSliderKeyIsStep}
        content={'This increments every time you call wandb.log in your script'}
      />
      <SliderInput
        min={min}
        max={max}
        minLabel=""
        maxLabel=""
        step={shouldStepSliderAllowAnyValue ? 'any' : 1}
        ticks={snapToExistingStep ? steps : undefined}
        value={current}
        keyboardBindings={keyboardBindings}
        strideLength={strideLength}
        hasInput
        debounceTime={10}
        onChange={handleChange}
        showTicks={false}
      />
    </div>
  );
};
